import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AppContext } from '../context/AppContext';
import { PlusIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const RecentContacts = () => {
  const { token } = useContext(AppContext);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContacts();
  }, [token]);

  const fetchContacts = async () => {
    try {
      const response = await fetch('/api/contacts', {
        headers: { Authorization: token }
      });
      if (response.ok) {
        const data = await response.json();
        // Handle both array response (all=true) and paginated response
        const contactsList = Array.isArray(data) ? data : (data.contacts || []);
        // Sort by updatedAt descending to show most recently modified first
        const sorted = contactsList.sort((a, b) =>
          new Date(b.updatedAt) - new Date(a.updatedAt)
        );
        setContacts(sorted.slice(0, 5));
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg bg-white p-4 shadow ring-1 ring-gray-200">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white shadow ring-1 ring-gray-200">
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-900">Recently Modified</h3>
          <Link
            to="/contacts?action=new"
            className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-500"
          >
            <PlusIcon className="h-4 w-4" />
            Add Contact
          </Link>
        </div>
      </div>

      {contacts.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <p className="text-sm text-gray-500">No contacts yet.</p>
          <Link
            to="/contacts?action=new"
            className="mt-2 inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
          >
            <PlusIcon className="h-4 w-4 mr-1" />
            Create your first contact
          </Link>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {contacts.map((contact) => (
            <li key={contact.id}>
              <Link
                to={`/contacts?action=edit&id=${contact.id}`}
                className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {contact.firstName} {contact.lastName}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {contact.emailAddress || contact.phoneNumber || 'No contact info'}
                  </p>
                </div>
                <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
              </Link>
            </li>
          ))}
        </ul>
      )}

      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
        <Link
          to="/contacts"
          className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
        >
          View all contacts
        </Link>
      </div>
    </div>
  );
};

export default RecentContacts;
